import React, { useState } from "react";
import { submitQuiz } from "@/lib/api";
import { useLearn } from "@/contexts/LearnContext";
import { CheckCircle2, XCircle, RotateCcw } from "lucide-react";

export default function QuizBlock({ moduleId, quiz, index }) {
  const { user, getQuizResult, recordQuiz } = useLearn();
  const prior = getQuizResult(moduleId, quiz.id);
  const [selected, setSelected] = useState(prior ? prior.selected_index : null);
  const [submitted, setSubmitted] = useState(!!prior);
  const [result, setResult] = useState(
    prior
      ? {
          is_correct: prior.is_correct,
          correct_index: prior.is_correct
            ? prior.selected_index
            : quiz.correct_index,
          explanation: quiz.explanation,
        }
      : null
  );
  const [reviewMode, setReviewMode] = useState(!!prior);

  const handleSubmit = async () => {
    if (selected === null || !user) return;
    const r = await submitQuiz(user.id, moduleId, quiz.id, selected);
    setResult(r);
    setSubmitted(true);
    setReviewMode(true);
    await recordQuiz();
  };

  const handleRetry = () => {
    setSelected(null);
    setSubmitted(false);
    setReviewMode(false);
    setResult(null);
  };

  const correctIdx = result?.correct_index ?? quiz.correct_index;

  return (
    <div
      className="border border-border rounded-md p-6 bg-card"
      data-testid={`quiz-${quiz.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
          Quiz · Question {index + 1}
        </span>
        {submitted && (
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium ${
              result?.is_correct ? "text-green-600 dark:text-green-400" : "text-destructive"
            }`}
          >
            {result?.is_correct ? (
              <>
                <CheckCircle2 size={14} /> Correct
              </>
            ) : (
              <>
                <XCircle size={14} /> Incorrect
              </>
            )}
          </span>
        )}
      </div>

      <h4 className="font-serif text-xl md:text-2xl leading-snug mb-5">
        {quiz.question}
      </h4>

      <div className="space-y-2">
        {quiz.options.map((opt, i) => {
          const isSelected = selected === i;
          const isCorrect = reviewMode && i === correctIdx;
          const isWrongPicked = reviewMode && isSelected && i !== correctIdx;
          return (
            <button
              type="button"
              key={i}
              disabled={submitted}
              onClick={() => setSelected(i)}
              data-testid={`quiz-option-${quiz.id}-${i}`}
              className={[
                "w-full text-left px-4 py-3 border rounded transition-all",
                "flex items-center gap-3",
                isCorrect
                  ? "border-green-500 bg-green-50 dark:bg-green-950/30"
                  : isWrongPicked
                  ? "border-destructive bg-destructive/5"
                  : isSelected
                  ? "border-primary bg-accent"
                  : "border-border hover:border-primary/60",
                submitted ? "cursor-default" : "cursor-pointer",
              ].join(" ")}
            >
              <span className="font-mono text-xs text-muted-foreground w-6">
                {String.fromCharCode(65 + i)}.
              </span>
              <span className="flex-1 text-sm">{opt}</span>
              {isCorrect && <CheckCircle2 size={16} className="text-green-600 dark:text-green-400" />}
              {isWrongPicked && <XCircle size={16} className="text-destructive" />}
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex items-center gap-3">
        {!submitted ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={selected === null}
            data-testid={`quiz-submit-${quiz.id}`}
            className="px-5 py-2 bg-primary text-primary-foreground text-sm font-medium rounded hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit answer
          </button>
        ) : (
          <button
            type="button"
            onClick={handleRetry}
            data-testid={`quiz-retry-${quiz.id}`}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs border border-border rounded hover:border-primary"
          >
            <RotateCcw size={13} /> Try again
          </button>
        )}
      </div>

      {reviewMode && result?.explanation && (
        <div
          className="mt-4 p-4 rounded bg-muted text-sm"
          data-testid={`quiz-explanation-${quiz.id}`}
        >
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
            Why this answer
          </div>
          <p className="text-foreground/90">{result.explanation}</p>
        </div>
      )}
    </div>
  );
}
